/**
 * Le délai privé : ce que le service a répondu à la dernière annonce partie.
 *
 * # Pourquoi la dernière partie, et pas la dernière arrivée
 *
 * Les annonces partent en cadence, et aussitôt que le régime change (voir
 * `LiveSession`). Leurs réponses voyagent chacune de son côté, et rien ne les
 * ramène dans l'ordre. Or une réponse dit le délai du régime **que le service
 * voyait en la calculant** : celle d'une annonce partie juste avant que le
 * lead change de mains dit encore le trajet depuis l'ancien leader.
 *
 * Retenir la dernière arrivée laisserait cette réponse-là écraser la bonne si
 * elle revenait après elle. Retenir la plus récente arrivée jusqu'ici la
 * laisserait encore passer si elle revenait avant : le transport poserait
 * l'ancien délai, puis le nouveau, et sa tête de lecture sauterait deux fois
 * au lieu d'une (voir `LocalTransport.setPlayoutDelay`). Toute annonce qui
 * part rend donc caduques les réponses de celles d'avant.
 *
 * # Ce qui ne change pas le délai
 *
 * Une annonce refusée ou perdue, une réponse illisible : rien de tout ça ne
 * dit que le délai a changé, et le délai tenu reste. Le remettre à zéro ferait
 * sauter la tête pour rien, et la réponse suivante la ferait sauter en retour.
 *
 * # Ce que ça coûte
 *
 * Une réponse qui met plus longtemps à revenir que la cadence des annonces
 * n'est jamais retenue. Hors jam, la cadence est de deux secondes, et un
 * service aussi lent n'a plus de délai utile à rendre ; en jam, le délai se lit
 * dans l'état publié, pas ici (voir `ownDelayMs`).
 */
export class PrivateDelay {
  /** Le numéro de la dernière annonce partie. */
  private departed = 0;

  private held: number | null = null;

  /**
   * Le délai tenu, en millisecondes. `null` tant qu'aucune réponse n'a été
   * retenue depuis le début de la séance.
   */
  get delayMs(): number | null {
    return this.held;
  }

  /**
   * Une annonce part. Rend le numéro qui reconnaîtra sa réponse, et rend
   * caduques les réponses des annonces parties avant elle.
   */
  depart(): number {
    this.departed += 1;

    return this.departed;
  }

  /**
   * La réponse de l'annonce `departure` arrive, avec le délai qu'elle porte.
   *
   * Retenue si elle répond à la dernière annonce partie et porte un délai.
   * Rend vrai quand elle l'a été.
   */
  settle(departure: number, delayMs: number | null): boolean {
    if (departure !== this.departed || delayMs === null) {
      return false;
    }

    this.held = delayMs;

    return true;
  }

  /**
   * Oublie le délai, et les réponses encore en vol : une séance quittée ne
   * laisse rien à la suivante.
   */
  reset(): void {
    this.departed += 1;
    this.held = null;
  }
}
